import { useMemo, useRef, useState, useEffect } from "react";
import { Cable, Cloud, ZoomIn, ZoomOut, Maximize2 } from "lucide-react";

// ---- Device icons ----
// User-supplied artwork (workstation, layer-2 switch, layer-3 switch, router).
// We render them as <image> in the SVG so they stay crisp at any zoom and
// stay totally faithful to the supplied designs — no recreations.
const ICON_URL = {
  router: "https://customer-assets.emergentagent.com/job_cert-bootcamp/artifacts/rplf6ecn_router.png",
  switch: "https://customer-assets.emergentagent.com/job_cert-bootcamp/artifacts/5d9xrd1n_layer%202switch.png",
  l3switch: "https://customer-assets.emergentagent.com/job_cert-bootcamp/artifacts/7lnkrktw_layer-3-switch.png",
  pc: "https://customer-assets.emergentagent.com/job_cert-bootcamp/artifacts/rpru0j7c_workstation.png",
};

function iconUrlFor(type) {
  if (type === "l3switch" || type === "layer3-switch") return ICON_URL.l3switch;
  if (type === "switch" || type === "l2switch") return ICON_URL.switch;
  if (type === "router") return ICON_URL.router;
  if (type === "pc" || type === "workstation" || type === "host") return ICON_URL.pc;
  return null;
}

const NODE_W = 96;
const NODE_H = 110;
// Generous padding around the topology so router/PC labels and link IP labels
// (e.g., "192.168.10.0/26") are never clipped at the SVG boundary.
const PAD = 160;
// Width of the device-label text box. Bigger than NODE_W so labels can
// extend beyond the icon without being truncated; centred via negative x.
const LABEL_W = 200;

export default function TopologyDiagram({ topology, activeDeviceIds = [], onDeviceClick = null, dualMode = false }) {
  // Compute device positions and the auto-fit viewBox.
  const computed = useMemo(() => {
    if (!topology || !topology.devices || topology.devices.length === 0) return null;
    const n = topology.devices.length;
    // distribute evenly if positions aren't supplied
    const baseX = 200;
    const baseY = 200;
    const dx = 220;
    const devices = topology.devices.map((d, i) => {
      const fallbackX = baseX + i * dx;
      return { ...d, x: d.x ?? fallbackX, y: d.y ?? baseY };
    });
    // bounding box
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    devices.forEach((d) => {
      minX = Math.min(minX, d.x - NODE_W / 2);
      minY = Math.min(minY, d.y - NODE_H / 2);
      maxX = Math.max(maxX, d.x + NODE_W / 2);
      maxY = Math.max(maxY, d.y + NODE_H / 2);
    });
    const vb = {
      x: minX - PAD,
      y: minY - PAD,
      w: Math.max(maxX - minX + PAD * 2, 360),
      h: Math.max(maxY - minY + PAD * 2, 220),
    };
    return { devices, byId: Object.fromEntries(devices.map((d) => [d.id, d])), vb };
  }, [topology]);

  const [zoom, setZoom] = useState(1.5);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const panStart = useRef(null);

  // Reset view when topology changes — 1.5x baseline so icons are large
  // enough to read at lab-open time. Auto-fit is provided by the SVG
  // viewBox; this just scales the rendered group on top of that.
  useEffect(() => {
    setZoom(1.5);
    setPan({ x: 0, y: 0 });
  }, [topology]);

  const onWheel = (e) => {
    e.preventDefault();
    const direction = e.deltaY > 0 ? -1 : 1;
    setZoom((z) => Math.max(0.4, Math.min(3, z + direction * 0.12)));
  };

  const onMouseDown = (e) => {
    if (e.button !== 0) return;
    panStart.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
  };
  const onMouseMove = (e) => {
    if (!panStart.current) return;
    setPan({
      x: panStart.current.panX + (e.clientX - panStart.current.x),
      y: panStart.current.panY + (e.clientY - panStart.current.y),
    });
  };
  const onMouseUp = () => {
    panStart.current = null;
  };
  const resetView = () => {
    setZoom(1.5);
    setPan({ x: 0, y: 0 });
  };

  if (!computed) {
    return (
      <div
        className="h-full flex items-center justify-center text-zinc-600 text-xs uppercase tracking-widest font-mono"
        style={{ background: "var(--topo-bg)" }}
        data-testid="topology-empty"
      >
        no topology
      </div>
    );
  }

  const { devices, byId, vb } = computed;
  const nodeBorder = (type) => {
    if (type === "switch") return "var(--topo-node-border-switch)";
    if (type === "router") return "var(--topo-node-border-router)";
    return "var(--topo-node-border-default)";
  };

  return (
    <div
      className="h-full w-full grid-bg-soft relative overflow-hidden"
      style={{ background: "var(--topo-bg)" }}
      data-testid="topology-diagram"
      onWheel={onWheel}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
    >
      <div className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.25em] font-mono" style={{ color: "var(--topo-link-label)" }}>
        topology
      </div>
      {/* Zoom controls */}
      <div className="absolute top-3 right-3 flex items-center gap-1 z-10" data-testid="topology-controls">
        <button
          onClick={() => setZoom((z) => Math.min(3, z + 0.2))}
          title="Zoom in"
          className="p-1.5 bg-[#141416] border border-zinc-800 hover:border-[#00E5FF] text-zinc-300 hover:text-white"
          data-testid="topology-zoom-in"
        >
          <ZoomIn size={12} />
        </button>
        <button
          onClick={() => setZoom((z) => Math.max(0.4, z - 0.2))}
          title="Zoom out"
          className="p-1.5 bg-[#141416] border border-zinc-800 hover:border-[#00E5FF] text-zinc-300 hover:text-white"
          data-testid="topology-zoom-out"
        >
          <ZoomOut size={12} />
        </button>
        <button
          onClick={resetView}
          title="Reset view"
          className="p-1.5 bg-[#141416] border border-zinc-800 hover:border-[#00E5FF] text-zinc-300 hover:text-white"
          data-testid="topology-reset"
        >
          <Maximize2 size={12} />
        </button>
        <span className="ml-1 text-[10px] uppercase tracking-widest text-zinc-500 font-mono" data-testid="topology-zoom-level">
          {Math.round(zoom * 100)}%
        </span>
      </div>
      <svg
        viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`}
        className="w-full h-full select-none"
        preserveAspectRatio="xMidYMid meet"
        style={{ cursor: panStart.current ? "grabbing" : "grab" }}
      >
        <g transform={`translate(${pan.x} ${pan.y}) scale(${zoom})`} style={{ transformOrigin: `${vb.x + vb.w / 2}px ${vb.y + vb.h / 2}px` }}>
          {(topology.links || []).map((l, i) => {
            const a = byId[l.from];
            const b = byId[l.to];
            if (!a || !b) return null;
            const mx = (a.x + b.x) / 2;
            const my = (a.y + b.y) / 2;
            const linkKey = `${l.from}-${l.to}-${i}`;
            return (
              <g key={linkKey}>
                <line
                  x1={a.x}
                  y1={a.y}
                  x2={b.x}
                  y2={b.y}
                  stroke="var(--topo-link)"
                  strokeWidth="1.5"
                  strokeDasharray="4 4"
                />
                {l.label && (
                  <text
                    x={mx}
                    y={my - 8}
                    fill="var(--topo-link-label)"
                    fontSize="11"
                    fontFamily="JetBrains Mono, monospace"
                    textAnchor="middle"
                    overflow="visible"
                    style={{ paintOrder: "stroke", stroke: "var(--topo-bg)", strokeWidth: 3 }}
                  >
                    {l.label}
                  </text>
                )}
              </g>
            );
          })}
          {devices.map((d) => {
            const isActive = activeDeviceIds.includes(d.id);
            const activeIndex = activeDeviceIds.indexOf(d.id);
            const clickable = !!onDeviceClick && !!iconUrlFor(d.type);
            const url = iconUrlFor(d.type);
            const ICON_SIZE = 64; // square image area inside the node box (per user spec)
            return (
              <g
                key={d.id}
                transform={`translate(${d.x - NODE_W / 2} ${d.y - NODE_H / 2})`}
                style={{ cursor: clickable ? "pointer" : "default" }}
                onMouseDown={(e) => clickable && e.stopPropagation()}
                onClick={(e) => {
                  if (!clickable) return;
                  e.stopPropagation();
                  onDeviceClick(d.id, {
                    secondary: e.shiftKey && dualMode,
                    clientX: e.clientX,
                    clientY: e.clientY,
                    deviceType: d.type,
                  });
                }}
                data-testid={`topology-node-${d.id}`}
              >
                {/* Active glow — the only visual chrome left around the icon. */}
                {isActive && (
                  <circle
                    cx={NODE_W / 2}
                    cy={ICON_SIZE / 2 + 6}
                    r={ICON_SIZE / 2 + 8}
                    fill="none"
                    stroke={activeIndex === 0 ? "#00E5FF" : "#F59E0B"}
                    strokeWidth="3"
                    opacity="0.85"
                  >
                    <animate attributeName="opacity" values="0.4;1;0.4" dur="2s" repeatCount="indefinite" />
                  </circle>
                )}
                {url ? (
                  <image
                    href={url}
                    xlinkHref={url}
                    x={(NODE_W - ICON_SIZE) / 2}
                    y={6}
                    width={ICON_SIZE}
                    height={ICON_SIZE}
                    preserveAspectRatio="xMidYMid meet"
                  />
                ) : (
                  <foreignObject width={NODE_W} height={ICON_SIZE} x="0" y="6">
                    <div
                      className="w-full h-full flex items-center justify-center"
                      xmlns="http://www.w3.org/1999/xhtml"
                      style={{ color: "var(--topo-node-label)" }}
                    >
                      {d.type === "cloud" ? <Cloud size={36} /> : <Cable size={36} />}
                    </div>
                  </foreignObject>
                )}
                <foreignObject
                  width={LABEL_W}
                  height={36}
                  x={(NODE_W - LABEL_W) / 2}
                  y={ICON_SIZE + 8}
                  overflow="visible"
                >
                  <div
                    className="w-full text-center font-mono"
                    xmlns="http://www.w3.org/1999/xhtml"
                    style={{
                      color: "var(--topo-node-label)",
                      fontSize: 12,
                      fontWeight: 600,
                      letterSpacing: "0.04em",
                      whiteSpace: "nowrap",
                      overflow: "visible",
                    }}
                  >
                    {d.label || d.id}
                  </div>
                </foreignObject>
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
}
